<template>
  <div class="mall-wish" :class="{ 'is-embedded': isEmbedded }">
    <div class="mall-wish__shell">
      <header class="mall-wish__head">
        <div>
          <h1>찜하기</h1>
          <p>{{ wishlist.length }}개</p>
        </div>
        <div v-if="wishlist.length" class="mall-wish__bulk no-print">
          <label class="mall-wish__check-all">
            <input
              type="checkbox"
              :checked="allSelectableChecked"
              :indeterminate.prop="partialSelected"
              @change="toggleSelectAll"
            />
            전체선택
          </label>
          <button
            type="button"
            class="mall-wish__cart-btn"
            :disabled="!selectedIds.length || adding"
            @click="addSelectedToCart"
          >
            <AppIcon icon="mdi-cart-outline" :size="18" />
            {{ adding ? '담는 중…' : `선택담기 (${selectedIds.length})` }}
          </button>
          <button
            type="button"
            class="mall-wish__ghost-btn"
            :disabled="!selectedIds.length"
            @click="removeSelected"
          >
            선택삭제
          </button>
          <button
            v-if="issueCount"
            type="button"
            class="mall-wish__ghost-btn"
            @click="removeIssues"
          >
            품절상품삭제
          </button>
        </div>
      </header>

      <p v-if="issueCount" class="mall-wish__alert">
        <AppIcon icon="mdi-alert-outline" :size="16" />
        품절·판매종료 상품이 {{ issueCount }}개 있습니다. 판매중인 상품만 장바구니에 담을 수 있습니다.
      </p>

      <div v-if="!wishlist.length" class="mall-wish__empty">
        <AppIcon icon="mdi-heart-off-outline" :size="56" />
        <p>찜한 상품이 없습니다.</p>
        <em>상품의 찜하기 버튼을 누르면 여기에 저장됩니다.</em>
        <router-link to="/mall">쇼핑계속하기</router-link>
      </div>

      <div v-else class="mall-wish__grid">
        <article
          v-for="row in rows"
          :key="row.id"
          class="mall-wish__card"
          :class="{ 'is-issue': row.issue, 'is-selected': selectedIds.includes(row.id) }"
        >
          <label class="mall-wish__select">
            <input
              type="checkbox"
              :checked="selectedIds.includes(row.id)"
              :aria-label="`${row.product?.name || row.id} 선택`"
              @change="toggleSelect(row.id)"
            />
          </label>
          <ProductCard v-if="row.product" :product="row.product" />
          <div v-else class="mall-wish__missing">
            <AppIcon icon="mdi-package-variant-remove" :size="28" />
            <p>판매가 종료된 상품입니다.</p>
            <button type="button" @click="removeOne(row.id)">삭제</button>
          </div>
          <p v-if="row.product?.soldOut" class="mall-wish__badge">품절</p>
        </article>
      </div>
    </div>

    <div v-if="undoMessage" class="mall-wish__undo" role="status">
      <span>{{ undoMessage }}</span>
      <button type="button" @click="undoWish">실행 취소</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import ProductCard from '@/components/mall/ProductCard.vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { ensureLiveProducts } from '@/api/mall'
import { useCart } from '@/composables/useCart'
import { useMallMypageEmbedded } from '@/composables/useMallMypage'
import { useMallToast } from '@/composables/useMallToast'
import { useWishlist } from '@/composables/useWishlist'
import { getProductById } from '@/constants/mallProducts'

const isEmbedded = useMallMypageEmbedded()
const { wishlist, removeWish, removeMany, addMany } = useWishlist()
const { addItem } = useCart()
const { showSuccess, showError } = useMallToast()

const selectedIds = ref<string[]>([])
const adding = ref(false)
const undoMessage = ref('')
let undoTimer: ReturnType<typeof setTimeout> | undefined
let lastRemoved: string[] = []

const rows = computed(() =>
  wishlist.value.map((id) => {
    const product = getProductById(id) || null
    return {
      id,
      product,
      issue: !product || Boolean(product.soldOut),
    }
  }),
)

const selectableIds = computed(() => rows.value.filter((row) => !row.issue).map((row) => row.id))
const issueCount = computed(() => rows.value.filter((row) => row.issue).length)
const allSelectableChecked = computed(
  () =>
    rows.value.length > 0 && rows.value.every((row) => selectedIds.value.includes(row.id)),
)
const partialSelected = computed(
  () => selectedIds.value.length > 0 && !allSelectableChecked.value,
)

function toggleSelect(id: string) {
  if (selectedIds.value.includes(id)) {
    selectedIds.value = selectedIds.value.filter((item) => item !== id)
  } else {
    selectedIds.value = [...selectedIds.value, id]
  }
}

function toggleSelectAll(event: Event) {
  const checked = (event.target as HTMLInputElement).checked
  selectedIds.value = checked ? rows.value.map((row) => row.id) : []
}

function rememberUndo(ids: string[], message: string) {
  lastRemoved = ids
  undoMessage.value = message
  window.clearTimeout(undoTimer)
  undoTimer = setTimeout(() => {
    undoMessage.value = ''
    lastRemoved = []
  }, 5000)
}

function removeOne(id: string) {
  removeWish(id)
  selectedIds.value = selectedIds.value.filter((item) => item !== id)
  rememberUndo([id], '찜을 해제했습니다.')
}

function removeSelected() {
  if (!selectedIds.value.length) return
  const ids = [...selectedIds.value]
  removeMany(ids)
  selectedIds.value = []
  rememberUndo(ids, `${ids.length}개 상품의 찜을 해제했습니다.`)
}

function removeIssues() {
  const ids = rows.value.filter((row) => row.issue).map((row) => row.id)
  if (!ids.length) return
  removeMany(ids)
  selectedIds.value = selectedIds.value.filter((id) => !ids.includes(id))
  rememberUndo(ids, `품절상품 ${ids.length}개를 삭제했습니다.`)
}

function undoWish() {
  if (!lastRemoved.length) return
  addMany(lastRemoved)
  undoMessage.value = ''
  lastRemoved = []
  window.clearTimeout(undoTimer)
}

async function addSelectedToCart() {
  const ids = selectedIds.value.filter((id) => selectableIds.value.includes(id))
  if (!ids.length || adding.value) return
  adding.value = true
  try {
    for (const id of ids) {
      await addItem(id, 1)
    }
    selectedIds.value = []
    showSuccess(`${ids.length}개 상품을 장바구니에 담았습니다.`)
  } catch (error) {
    showError(error)
  } finally {
    adding.value = false
  }
}

watch(wishlist, () => {
  const alive = new Set(wishlist.value)
  selectedIds.value = selectedIds.value.filter((id) => alive.has(id))
})

onMounted(() => {
  void ensureLiveProducts()
})
</script>

<style lang="scss" src="./MallWishlistPage.scss"></style>
